import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import { Alert, Button } from 'react-bootstrap';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import CustomerService from 'services/modules/CustomerService';
import ModalUtils from 'utils/ModalUtils';
import MergeDialog from 'components/modules/customer/merge/MergeDialog';

const useStyles = makeStyles((theme) => ({
    banner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        padding: theme.spacing(1.25, 2),
        width: '100%',
        flexShrink: 0,
        minHeight: 48,
        boxSizing: 'border-box',
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0,
        flex: 1,
    },
    iconWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 0,
    },
    icon: {
        color: 'inherit',
        fontSize: '1.25rem',
    },
    message: {
        fontSize: '0.9rem',
        lineHeight: 1.25,
        color: 'inherit',
        margin: 0,
    },
    actionButton: {
        flexShrink: 0,
        fontWeight: 600,
    },
}));

const MergeBanner = ({ customerCode, duplicates: propDuplicates, onMergeCompleted }) => {
    const classes = useStyles();
    const canMerge = KeyStore.isAllowed(Permission.CUSTOMER_MERGE);
    const customerService = useMemo(() => new CustomerService(), []);

    const [localDuplicates, setLocalDuplicates] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        let active = true;
        if (!customerCode || !canMerge || propDuplicates != null) return undefined;

        customerService.getCustomerDuplicatesForMerge(customerCode)
            .then((response) => {
                if (!active) return;
                const { data, graphQLErrors } = response;
                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }
                setLocalDuplicates(data?.getCustomerDuplicatesForMerge || []);
            })
            .catch((error) => {
                if (!active) return;
                ModalUtils.errorMessage(error?.graphQLErrors || [error]);
            });

        return () => {
            active = false;
        };
    }, [customerCode, canMerge, customerService, propDuplicates]);

    const duplicates = propDuplicates != null ? propDuplicates : localDuplicates;

    if (!canMerge || duplicates.length === 0) return null;

    return (
        <>
            <Alert variant="danger" className={classes.banner}>
                <div className={classes.content}>
                    <span className={classes.iconWrap}>
                        <WarningOutlinedIcon className={classes.icon} />
                    </span>
                    <span className={classes.message}>
                        <strong>Possible Duplicates Found:</strong>
                        {` We found ${duplicates.length} potential duplicate record${duplicates.length === 1 ? '' : 's'} for this customer.`}
                    </span>
                </div>
                <Button
                    variant="primary"
                    size="sm"
                    className={classes.actionButton}
                    onClick={() => setOpenDialog(true)}
                >
                    Review and Merge
                </Button>
            </Alert>
            {openDialog && (
                <MergeDialog
                    open={openDialog}
                    masterCustomerCode={Number(customerCode)}
                    secondaryCustomerCodes={duplicates.map((item) => Number(item.customerCode))}
                    suggestedCustomer={duplicates}
                    showReviewStep
                    onClose={() => setOpenDialog(false)}
                    onMergeCompleted={onMergeCompleted}
                />
            )}
        </>
    );
};

MergeBanner.propTypes = {
    customerCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    duplicates: PropTypes.array,
    onMergeCompleted: PropTypes.func,
};

MergeBanner.defaultProps = {
    duplicates: null,
    onMergeCompleted: () => {},
};

export default MergeBanner;
